import React, { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import {
  formatDisplayDate,
  useIsMobile,
  useToast,
} from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useSubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
// Deep import rather than the appointments barrel, which pulls the whole
// client Appointment tab in behind whatever is taken from it — the same
// reasoning My task's own page gives for its five dialogs.
import ScheduleAppointmentModal from "../../appointments/components/ScheduleAppointmentModal";
import {
  appointmentShortDate,
  appointmentTime,
  attendeeName,
} from "../../appointments/appointments";
import type {
  AppointmentAttendee,
  NewClientAppointment,
} from "../../appointments/types/appointments.types";
import { useDayTasks } from "../hooks/useDayTasks";
import { atMidnight, dayFromParam, dayParam, isPastDay } from "../myTask";
import { ROUTES } from "../../../routes/paths";

/** Back to the day this was opened from, with the day still on it. */
const dayUrl = (day: Date): string =>
  `${ROUTES.myTask}?date=${dayParam(day)}`;

/**
 * Booking an appointment, as a phone's own screen.
 *
 * It is the same form My task opens in a dialog from `md` up — the same
 * component, in its third shell. What this page adds is everything around
 * that form: a heading with a way out, the browser history entry that makes
 * the phone's back gesture the way out too, and the write itself.
 *
 * ## Why a page and not the drawer it was
 *
 * The booking form asks eight questions, one of which is a paragraph. On a
 * phone that came up as a bottom sheet nine tenths of the height of the
 * screen, with a grabber at the top implying it could be dragged and a strip
 * of the day showing above it implying the day was still there to be read.
 * Neither was true. A drawer that covers the screen, scrolls, and holds a
 * keyboard open over a five-hundred-character box is a page that has not
 * admitted it, and the cost of the pretence is real: no history entry, so the
 * back gesture left the whole screen instead of the form, and the two buttons
 * pinned under a sheet rather than under the app.
 *
 * From `md` up the dialog is still right and is still what My task opens —
 * there the day stays visible behind a panel one glance wide, which is the
 * whole point of a dialog. See `ROUTES.newTask`.
 *
 * ## The day
 *
 * In `?date=`, both ways. The form opens on the day My task was standing on
 * — walking to Friday and pressing New task *is* the answer to "which day",
 * and a form that came up on today while Friday was lit behind it would be
 * asking somebody to repeat themselves. And the way out carries it back, so
 * cancelling returns to Friday rather than to this morning. That round trip
 * is why My task keeps its day in the URL at all; `dayParam` has the rest of
 * it.
 *
 * A day that has gone is passed through as no day at all, the same way the
 * dialog handles it: the form refuses a date behind today, and opening with
 * one already in the field is an error under a question nobody has touched.
 *
 * ## What it writes to
 *
 * `useDayTasks` on the day in hand, for its `schedule` — which is the same
 * call the dialog made from My task, writing the booked row straight into
 * that day's cache and marking every other reader of it stale. So the day
 * this returns to already has the appointment on it, whichever day it landed
 * on; see `invalidateAppointmentReads`.
 */
const NewTaskPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const gate = useSubscriptionGate();
  const [params] = useSearchParams();

  /**
   * Read once. The clock has to keep still under a form somebody is filling
   * in — a page that re-derived "today" every render would, at midnight, send
   * the way out to a different day from the one it came from.
   */
  const [today] = useState(() => atMidnight(new Date()));
  const day = dayFromParam(params.get("date")) ?? today;

  const { schedule } = useDayTasks(day);
  const [saving, setSaving] = useState(false);

  /**
   * Which shell this URL deserves.
   *
   * Only a phone is ever sent here — My task opens the dialog from `md` up —
   * so a wide screen on this path is a deep link, a bookmark, or somebody who
   * widened the window with the form open. All three want the same answer:
   * the day, with the dialog over it, which is what My task draws for its own
   * Create. Replaced rather than pushed, so the back button does not walk
   * straight into a page that will bounce again.
   */
  const isMobile = useIsMobile();

  /**
   * And the lock. The button that leads here is already off while the
   * workspace has no plan behind it — see `lockedProps` on My task — but a
   * URL is not a button, and this one writes. The day says why, since that is
   * the screen carrying the locked Create.
   */
  const locked = gate.locked;

  if (!isMobile || locked) {
    return <Navigate to={dayUrl(day)} replace />;
  }

  const leave = () => navigate(dayUrl(day));

  const handleConfirm = async (
    input: NewClientAppointment,
    attendee?: AppointmentAttendee
  ) => {
    // No attendee is the one question this form always asks — it is opened
    // from a day rather than from a person — so there is nothing to book
    // without one.
    if (saving || !attendee) return;
    setSaving(true);

    try {
      const booked = await schedule(attendee, input);
      const who = attendeeName(booked);
      toast.success(
        "Appointment scheduled",
        `${appointmentShortDate(booked.startsAt)} at ${appointmentTime(
          booked.startsAt
        )}${who ? ` with ${who}` : ""}.`
      );

      // Onto the day it actually landed on, which the form allows to be any
      // day from today: leaving somebody on a list their new appointment is
      // plainly not in is the one answer that is wrong here. A bad date
      // cannot reach this line — the form will not submit one — so the fall
      // back is only ever for a reply that came back without a readable
      // `startsAt`.
      const landed = new Date(booked.startsAt);
      navigate(dayUrl(Number.isNaN(landed.getTime()) ? day : landed));
    } catch (cause) {
      // The form stays up with everything still in it, the way the dialog
      // does: a booking that could not be made is one to send again, not one
      // to retype.
      toast.error(
        "Could not schedule this appointment",
        cause instanceof ApiError
          ? cause.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("could not schedule this appointment:", cause);
      setSaving(false);
    }
  };

  return (
    // The page grows with the form rather than being pinned to the viewport,
    // so what scrolls is the whole page under the shell's own scrollbar —
    // the shape every other form screen in the app takes, and what lets the
    // action bar come to rest on the foot of the screen. No padding at the
    // bottom: the bar's own is the last thing on the screen.
    <div className="flex min-h-full flex-col px-page pt-page-top">
      <PageHeader
        title="Create meeting"
        // Named rather than left to the trail or the history. The arrow has
        // to go back to the *day*, which neither of those knows: a form
        // opened on Friday and reached by a link has no history under it, and
        // the trail would only say "My task".
        back={dayUrl(day)}
      />

      <div className="mt-4">
        <ScheduleAppointmentModal
          shell="page"
          pickClient
          // Blank on a day that has gone — see the note on the panel above.
          initialDate={isPastDay(day, today) ? undefined : formatDisplayDate(day)}
          busy={saving}
          onClose={leave}
          onConfirm={handleConfirm}
        />
      </div>
    </div>
  );
};

export default NewTaskPage;
